import { computed, inject, Inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoadingService } from '@lib/services/loading.service';
import { AbstractAuthService, QueryManagerService, UserInfo, UserParametersService } from 'finzzle-angular-utils';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import {
  Consultant,
  ConsultantByCodeGQL,
  ConsultantHabilitation,
  PersonnePhysique,
  UpdateDerStateFromConsultantDataGQL,
} from '../../public-api';
import { filterHabilitations } from '../utils/habilitation';
import { CanAccessMinotaureAppGQL } from './../models/generated/graphql';
import { DocumentsService } from './documents.service';
import { LayoutService } from './layout.service';
const CHECK_HABILIATIONS_CODES = ['ASSU', 'IMMO', 'CIF', 'ALPSI', 'IOB', 'FINAN'];
// Class handling the login process, token decoding, and user information retrieval
@Injectable({
  providedIn: 'root',
})
export class AuthService extends AbstractAuthService {
  private queryManagerService = inject(QueryManagerService);
  private LayoutService = inject(LayoutService);
  private loadingService = inject(LoadingService);
  private documentService = inject(DocumentsService);
  private readonly consultantByCodeGQL = inject(ConsultantByCodeGQL);
  private readonly updateDERStateFromConsultantDataGQL = inject(UpdateDerStateFromConsultantDataGQL);
  private readonly appAccessRightGQL = inject(CanAccessMinotaureAppGQL);
  isBackOfficeValue = false;
  isAdminSubject = new BehaviorSubject<boolean>(false);
  public isAdmin = toSignal(this.isAdminSubject, { requireSync: true });
  public readonly userNotificationsSubject = new BehaviorSubject<Notification[]>([]);
  userHabilitationCheck: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public userConsultantHasNoHabilitations = signal<boolean>(false);
  private userConsultantValidHabilitations = signal<ConsultantHabilitation[]>([]);
  private userConsultantExpiredHabilitations = signal<ConsultantHabilitation[]>([]);
  public userConsultantHasNoRequiredHabilitations = computed<boolean>(
    () => this.userConsultantValidHabilitations()?.length === 0 && this.userConsultantExpiredHabilitations()?.length === 0
  );
  public userConsultantHasAllHabilitationsExpired = computed<boolean>(
    () => this.userConsultantValidHabilitations()?.length === 0 && this.userConsultantExpiredHabilitations()?.length > 0
  );
  public displayModelesDocuments = computed<boolean>(() => {
    if (this.isBackOffice()) {
      return true;
    }
    const cifHabiliation = this.userConsultantValidHabilitations()?.find(h => h.codeHabilitation === 'CIF');
    if (cifHabiliation) {
      return cifHabiliation.cifPartenaireCode?.includes('ANACOFI') ?? false;
    }
    return false;
  });
  public userConsultantId: number | null = null;
  public consultantProfilePicture = signal<string>('');
  public consultant = signal<Consultant | null>(null);
  public consultantPersonnePhysique = signal<PersonnePhysique | null>(null);
  public consultantPhones = computed<string[]>(() => {
    const phones = [...(this.consultantPersonnePhysique()?.telephone ?? []), ...(this.consultant()?.telephone ?? [])]
      .filter(t => t?.estNumeroCorrespondance !== false)
      .map(t => t?.numero)
      .filter(Boolean);
    return [...new Set(phones)];
  });
  public consultantEmails = computed<string[]>(() => {
    const emails = [...(this.consultantPersonnePhysique()?.email ?? []), ...(this.consultant()?.email ?? [])]
      .filter(t => t?.estAdresseCorrespondance !== false)
      .map(t => t?.adresse)
      .filter(Boolean);
    return [...new Set(emails)];
  });
  private readonly _canAccessApp = signal<boolean>(true);
  public readonly canAccessApp = this._canAccessApp.asReadonly();
  private readonly _accessCheckError = signal<boolean>(false);
  public readonly accessCheckError = this._accessCheckError.asReadonly();
  private readonly _accessRightChecked = signal<boolean>(false);
  public readonly accessRightChecked = this._accessRightChecked.asReadonly();
  public isAbonnementB2SValid = computed<boolean>(() => {
    const abonnement = this.consultant()?.abonnementConsultant?.find(ac => ac.referenceProduit === 'B2S');
    if (!abonnement) {
      return false;
    }
    return abonnement.expirationAcces === null || new Date(abonnement.expirationAcces).getTime() > Date.now();
  });
  public hasFeatureTag(tag: string): boolean {
    const featureTags = this.environment?.featureTags;

    if (Array.isArray(featureTags)) {
      return featureTags.includes(tag);
    }

    if (typeof featureTags === 'string') {
      return featureTags === tag;
    }

    return false;
  }
  private readonly _isContratViewer = signal<boolean>(false);
  public readonly isContratViewer = this._isContratViewer.asReadonly();
  public _isMigrationViewer = signal<boolean>(false);
  public readonly isMigrationViewer = this._isMigrationViewer.asReadonly();
  constructor(
    @Inject('environment')
    environment: any,
    userParametersService: UserParametersService
  ) {
    super(environment, userParametersService);
  }
  get consultantId(): number | null {
    return this.userConsultantId;
  }
  onNewValidToken() {
    if (!this.isBackOfficeValue) {
      this.initAccessRights();
    } else {
      this.userHabilitationCheck.next(true);
      this.LayoutService.showMenu();
      this.LayoutService.showHeader();
      this._accessRightChecked.set(true);
    }
  }
  private async initAccessRights() {
    try {
      const accessRight = await this.checkAppAccessRight();
      this._canAccessApp.set(accessRight);
      if (accessRight) {
        this.getConsultantHabilitations();
      }
    } catch (error) {
      console.error(error);
      this._accessCheckError.set(true);
    } finally {
      this._accessRightChecked.set(true);
    }
  }
  isTokenValid(): boolean {
    const consultantGroups = this.environment?.authConfig?.consultantGroups ?? [];
    const backOfficeAdminGroups = this.environment?.authConfig?.backOfficeAdminGroups ?? [];
    const contratGroups = this.environment?.authConfig?.contratGroups ?? [];
    const migrationGroups = this.environment?.authConfig?.migrationGroups ?? [];

    const rawMemberOf = this.tokenInfo?.memberOf;

    let memberOf: string[] = [];
    if (Array.isArray(rawMemberOf)) {
      memberOf = rawMemberOf;
    } else if (typeof rawMemberOf === 'string') {
      memberOf = [rawMemberOf];
    }

    const isContratViewer = memberOf.some(value => contratGroups.some((expectedValue: string) => value.includes(expectedValue)));
    this._isContratViewer.set(isContratViewer);

    const isMigrationViewer = memberOf.some(value => migrationGroups.some((expectedValue: string) => value.includes(expectedValue)));
    this._isMigrationViewer.set(isMigrationViewer);

    this.isBackOfficeValue = isContratViewer || isMigrationViewer;

    const isAdminValue = memberOf.some(value => backOfficeAdminGroups.some((expectedValue: string) => value.includes(expectedValue)));
    this.isAdminSubject.next(isAdminValue);

    let isAuthent = false;
    if (!this.isBackOfficeValue) {
      isAuthent = memberOf.some(value => consultantGroups.some((expectedValue: string) => value.includes(expectedValue)));
    } else {
      isAuthent = true;
    }

    return isAuthent;
  }
  // Retrieves the user information extracted from the JWT token.
  getUserInfo(): UserInfo {
    return {
      givenName: this.tokenInfo?.givenName,
      lastName: this.tokenInfo?.lastName,
      fullName: this.tokenInfo?.givenName + ' ' + (this.tokenInfo?.lastName ?? this.tokenInfo?.sn ?? ''),
      email: this.tokenInfo?.sub,
      tokenId: this.tokenInfo?.id,
      consultantCode: this.tokenInfo?.consultantId,
    };
  }
  isAdminRole(): boolean {
    return this.isBackOffice() && this.isAdminSubject?.getValue();
  }
  isBackOffice(): boolean {
    return this.isBackOfficeValue;
  }
  override onReset(): void {
    this.isBackOfficeValue = false;
    this.isAdminSubject.next(false);
    this.userHabilitationCheck.next(false);
    this._accessRightChecked.set(false);
  }
  override logout(): void {
    sessionStorage.removeItem(AuthService.token_sessionstorage_key);
    window.location.href = this.environment.authConfig.logoutUrl;
  }
  override async login(): Promise<void> {
    try {
      await super.login();
      if (this.isAuthenticated() && !this.isBackOffice()) {
        this.updateDERStateFromConsultantData();
      }
    } catch (error) {
      console.error(error);
      this._accessCheckError.set(true);
    }
  }
  private getConsultantHabilitations(): void {
    this.loadingService.startLoading();
    this.queryManagerService
      .handleQueryErrorsAsync(
        this.consultantByCodeGQL.fetch({
          fetchPolicy: 'network-only',
          variables: { code: this.tokenInfo?.id },
        })
      )
      .then(result => {
        const userConsultant = result.data!.consultantByCode as Consultant;
        if (userConsultant) {
          this.consultant.set(userConsultant);
          const personnePhysique =
            userConsultant.personnePhysique?.find(pp => pp.code === this.tokenInfo?.personId) ??
            userConsultant.personnePhysique?.[0] ??
            null;
          this.consultantPersonnePhysique.set(personnePhysique);
          this.userConsultantHasNoHabilitations.set(userConsultant.consultantHabilitations?.length === 0);
          if (userConsultant.consultantHabilitations.length > 0) {
            const habs = filterHabilitations(userConsultant.consultantHabilitations, CHECK_HABILIATIONS_CODES);
            this.userConsultantValidHabilitations.set(habs.valid);
            this.userConsultantExpiredHabilitations.set(habs.expired);
            this.userConsultantId = userConsultant.id;
            if (this.userConsultantValidHabilitations()?.length > 0) {
              this.LayoutService.showMenu();
              this.LayoutService.showHeader();
              this.getConsultantProfilePicture(personnePhysique);
            }
          }
          this.userHabilitationCheck.next(true);
        }
      })
      .finally(() => this.loadingService.stopLoading());
  }
  private getConsultantProfilePicture(personnePhysique: PersonnePhysique | null): void {
    const documents = personnePhysique?.personnePhysiqueDocument;
    const pictureDocument = documents?.filter(d => d.codeTypeDocument === 'PHOTO_PORTRAIT')?.sort((a, b) => b.id - a.id)?.[0];
    if (pictureDocument?.documentId) {
      this.documentService.getFileMetadatasById(pictureDocument.documentId).then(d => {
        if (d?.url) {
          this.consultantProfilePicture.set(d.url);
        }
      });
    }
  }
  private async updateDERStateFromConsultantData(): Promise<void> {
    await this.queryManagerService.handleMutationErrorsAsync(this.updateDERStateFromConsultantDataGQL.mutate());
  }
  private async checkAppAccessRight(): Promise<boolean> {
    const result = await lastValueFrom(
      this.appAccessRightGQL.fetch({
        fetchPolicy: 'network-only',
        variables: {},
      })
    );
    return result.data!.minotaureAppAccessRight;
  }
}
